<template>
  <div :class="$style.listMenu">
    <div :class="$style.filter">
      <div :class="$style.button" @click="onClickOpenFilter">
        <span>{{ filterLabel }}</span>
      </div>
    </div>
    <div :class="$style.sort">
      <div :class="[$style.button, $style.buttonDropdown, isSortShow ? $style.active : '']" @click="onClickToggleSort">
        {{ sortLabel }}
      </div>
      <div>
        <ul>
          <li :class="{ selected: sort === 'new' }" @click="onClickSortItem('new')">新着順</li>
          <li :class="{ selected: sort === 'ending' }" @click="onClickSortItem('ending')">終了日が近い順</li>
        </ul>
      </div>
    </div>
  </div>
  <IModal
    v-model="isFilterShow"
    width="600px"
    :with-action-button="true"
    text-cancel="リセット"
    text-ok="検索結果を表示"
    :close-on-cancel="false"
    :close-on-ok="false"
    :class="$style.filterModal"
    @cancel="onClickResetFilter"
    @ok="onClickSubmitFilter"
  >
    <h4>開催日</h4>
    <div :class="[$style.filterSelect, $style.singleSelect]">
      <a :class="[period === undefined ? $style.selected : '']" @click.prevent="period = undefined">全て</a>
      <a :class="[period === 'ongoing' ? $style.selected : '']" @click.prevent="period = 'ongoing'">開催中</a>
      <a :class="[period === 'this-week' ? $style.selected : '']" @click.prevent="period = 'this-week'">今週</a>
      <a :class="[period === 'this-weekend' ? $style.selected : '']" @click.prevent="period = 'this-weekend'">今週末</a>
      <a :class="[period === 'this-month' ? $style.selected : '']" @click.prevent="period = 'this-month'">今月</a>
      <a :class="[period === 'next-month' ? $style.selected : '']" @click.prevent="period = 'next-month'">来月以降</a>
    </div>
    <h4>ジャンル</h4>
    <div :class="[$style.filterSelect, $style.multiSelect]">
      <a v-for="g in genreList" :key="g.slug" :class="[genres.includes(g.slug) ? $style.selected : '']" @click.prevent="onClickGenreItem(g.slug)">{{ g.name }}</a>
    </div>
    <h4>エリア</h4>
    <div :class="$style.filterSelect">
      <div :class="$style.dropdownWrapper">
        <select v-model="prefecture" :class="{ selected: Boolean(prefecture) }">
          <option :value="undefined">都道府県を選ぶ</option>
          <option v-for="p in prefectureList" :key="p.slug" :value="p.slug">
            {{ p.name }}
          </option>
        </select>
      </div>
    </div>
  </IModal>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { prefectures as prefectureList } from '@/js/data/area';
import genreList from '@/js/data/genre';
import IModal from '@/js/components/molecules/IModal.vue';

const props = withDefaults(
  defineProps<{
    initialPeriod?: string;
    initialGenres?: string[];
    initialPrefecture?: string;
    initialSort?: string;
  }>(),
  {
    initialPeriod: undefined,
    initialGenres: () => [],
    initialPrefecture: undefined,
    initialSort: 'new',
  },
);
const isFilterShow = ref(false);
const isSortShow = ref(false);
const period = ref(props.initialPeriod);
const genres = ref(props.initialGenres);
const prefecture = ref(props.initialPrefecture);
const sort = ref(props.initialSort);
const filterLabel = computed(() => {
  const conditionCount = (period.value === undefined ? 0 : 1) + genres.value.length + (prefecture.value === undefined ? 0 : 1);
  let label = '絞り込み';
  if (conditionCount > 0) {
    label += ` (${conditionCount})`;
  }
  return label;
});
const sortLabel = computed(() => (sort.value === 'ending' ? '終了日順' : '新着順'));
const queryString = computed(() => {
  const url = new URLSearchParams();

  if (period.value) {
    url.append('period', period.value);
  }
  if (genres.value.length > 0) {
    url.append('genre', genres.value.join(','));
  }
  if (prefecture.value) {
    url.append('prefecture', prefecture.value);
  }
  if (sort.value !== 'new') {
    url.append('sort', sort.value);
  }

  return url.toString();
});
const onClickOpenFilter = () => {
  isFilterShow.value = true;
  isSortShow.value = false;
};
const onClickResetFilter = () => {
  period.value = undefined;
  genres.value = [];
  prefecture.value = undefined;
};
const onClickSubmitFilter = () => {
  window.location.href = `?${queryString.value}`;
};
const onClickGenreItem = (slug: string) => {
  const index = genres.value.indexOf(slug);

  if (index === -1) {
    genres.value.push(slug);
  } else {
    genres.value.splice(index, 1);
  }
};
const onClickToggleSort = () => {
  isSortShow.value = !isSortShow.value;
  isFilterShow.value = false;
};
const onClickSortItem = (tmpSort: string) => {
  sort.value = tmpSort;
  isSortShow.value = false;
  window.location.href = `?${queryString.value}`;
};
</script>

<style module lang="scss">
.listMenu {
  display: flex;
  justify-content: space-between;

  .button {
    background-color: var(--color-white);
    border: 1px solid $border-color;
    color: #404040;
    height: 36px;
    line-height: 34px;
    padding: 0 10px;
    position: relative;
    width: 100%;
  }

  .filter {
    flex-grow: 2;

    .button {
      border-right: 0;
      cursor: pointer;
      text-align: center;

      span {
        display: inline-block;
        height: 34px;
        line-height: 34px;
        padding-left: 26px;
        position: relative;

        &::before {
          content: $icon-filter;
          display: inline-block;
          font-family: $font-icon;
          font-size: 16px;
          left: 2px;
          position: absolute;
          text-align: center;
          width: 16px;
        }
      }
    }
  }

  .sort {
    flex-grow: 1;
    position: relative;

    .buttonDropdown {
      cursor: pointer;

      &::after {
        color: #8e8e8e;
        content: $icon-arrow-bottom;
        font-family: $font-icon;
        font-size: 28px;
        position: absolute;
        right: 5px;
        transition: transform 0s ease-in-out;
      }

      & + div {
        background-color: var(--color-white);
        border: 1px solid $border-color;
        color: #404040;
        display: block;
        line-height: 36px;
        margin-top: -10px;
        opacity: 0;
        padding: 8px 0;
        position: absolute;
        right: 0;
        transition:
          opacity 0s ease-in-out,
          margin 0s ease-in-out,
          visibility 0s ease-in-out;
        visibility: hidden;
        white-space: nowrap;
        z-index: 2;

        li {
          cursor: pointer;
          padding-left: 16px;
          padding-right: 16px;

          &::after {
            color: #8e8e8e;
            content: '';
            display: inline-block;
            font-family: $font-icon;
            line-height: 30px;
            margin-left: 4px;
            width: 24px;
          }

          &.selected::after {
            content: $icon-check1;
          }
        }
      }

      &.active {
        &::after {
          transform: rotate(180deg);
          transition: transform 0.1s ease-in-out;
        }

        & + div {
          display: block;
          margin-top: 3px;
          opacity: 1;
          transition:
            opacity 0.1s ease-in-out,
            margin 0.1s ease-in-out,
            visibility 0.1s ease-in-out;
          visibility: visible;
        }
      }
    }
  }
}

.filterModal {
  h4 {
    color: #404040;
    font-size: 18px;
    font-weight: bold;
    margin: 24px 0 10px;
    text-align: left;

    &:first-child {
      margin-top: 5px;
    }
  }

  .filterSelect {
    a {
      color: #404040;
      padding: 6px 10px;
      text-align: center;
      text-decoration: none;
    }

    &.singleSelect {
      display: flex;
      flex-wrap: wrap;
      gap: 10px;

      a {
        background-color: var(--color-white);
        border: 1px solid $border-color;

        &::before {
          background-color: $border-color;
          border: 3px solid $border-color;
          border-radius: 50%;
          content: '';
          display: inline-block;
          height: 14px;
          line-height: 34px;
          margin-right: 5px;
          vertical-align: middle;
          width: 14px;
        }

        &.selected {
          border-color: #404040;

          &::before {
            background-color: #444;
          }
        }
      }
    }

    &.multiSelect {
      display: flex;
      flex-wrap: wrap;
      gap: 10px;

      a {
        background-color: $light-gray;
        border: 1px solid $light-gray;

        &.selected {
          border-color: #404040;
        }
      }
    }

    h5 {
      font-weight: normal;
      margin-bottom: -3px;
      margin-top: 5px;
    }

    .dropdownWrapper {
      position: relative;

      &::after {
        color: #8e8e8e;
        content: $icon-arrow-bottom;
        font-family: $font-icon;
        font-size: 28px;
        line-height: 36px;
        pointer-events: none;
        position: absolute;
        right: 5px;
        transition: transform 0.15s ease-in-out;
      }

      select {
        appearance: none;
        border: 1px solid $border-color;
        color: #404040;
        cursor: pointer;
        height: 36px;
        padding: 0 10px;
        width: 100%;

        &.selected {
          border-color: #404040;
        }
      }
    }
  }
}
</style>
